import { UTCDate } from '@date-fns/utc';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView } from '../../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { MetricCard } from '../../components/metrics/metric-card';
import { getAverageMonthlyBurnData } from '../../utils/get-average-monthly-burn-data';
import { getCurrentCashData } from '../../utils/get-current-cash-data';
import { getEstimatedRunwayData } from '../../utils/get-estimated-runway-data';
import { getMonthlyBurnData } from '../../utils/get-monthly-burn-data';
import { getRevenueData } from '../../utils/get-revenue-data';

type CashFlowMetricsProps = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
};

const CashFlowMetrics = ({ accounts, baseCurrency }: CashFlowMetricsProps) => {
  //TODO: Implement a timeframe prop from the filter
  const now = new UTCDate();
  const beginningOfMonth = startOfMonth(now);
  const startOfPreviousMonth = startOfMonth(subMonths(now, 1));
  const endOfPreviousMonth = endOfMonth(subMonths(now, 1));

  const currentCashData = getCurrentCashData({
    accounts,
    baseCurrency,
    startDate: beginningOfMonth,
    endDate: now,
  });

  const previousMonthBurnData = getMonthlyBurnData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: startOfPreviousMonth,
    currentPeriodEndDate: endOfPreviousMonth,
    previousPeriodStartDate: startOfMonth(subMonths(now, 2)),
    previousPeriodEndDate: startOfMonth(subMonths(now, 1)),
  });

  const estimatedRunwayData = getEstimatedRunwayData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: beginningOfMonth,
    currentPeriodEndDate: now,
    previousMonthStartDate: startOfPreviousMonth,
    previousMonthEndDate: endOfPreviousMonth,
  });

  const revenueData = getRevenueData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: beginningOfMonth,
    currentPeriodEndDate: now,
    previousPeriodStartDate: startOfPreviousMonth,
    previousPeriodEndDate: endOfPreviousMonth,
  });

  const average3MonthsBurnData = getAverageMonthlyBurnData({
    numberOfMonths: 3,
    accounts,
    baseCurrency,
  });

  const metrics = [
    {
      label: 'Current Cash',
      value: currentCashData.formattedCurrentCash,
      trend: currentCashData.percentageChange,
      direction: currentCashData.trend,
      subText: `from last month`,
      valueTone: currentCashData.valueTone,
    },
    {
      label: 'Monthly burn',
      value: previousMonthBurnData.formattedMonthlyBurn,
      trend: previousMonthBurnData.percentageChange,
      direction: previousMonthBurnData.trend,
      subText: `from ${format(subMonths(now, 2), 'MMMM')}`,
      valueTone: previousMonthBurnData.valueTone,
      tooltipText: <p className="text-xs">Gross burn: last calendar month's total spend excluding revenue</p>,
    },
    {
      label: 'Average burn',
      value: average3MonthsBurnData.formattedAverage3MonthsBurn,
      trend: average3MonthsBurnData.percentageChange,
      direction: average3MonthsBurnData.trend,
      subText: `vs prior period`,
      valueTone: average3MonthsBurnData.valueTone,
      tooltipText: (
        <Stack space={1}>
          <p className="text-xs">
            Average burn over the last 3 months ({format(subMonths(now, 3), 'MMMM')} -{' '}
            {format(subMonths(now, 1), 'MMMM')}).
          </p>
          <p className="text-xs">
            Compared to the previous period ({format(subMonths(now, 4), 'MMMM')} - {format(subMonths(now, 2), 'MMMM')}).
          </p>
        </Stack>
      ),
    },
    {
      label: 'Estimated Runway',
      value: estimatedRunwayData.formattedRunway,
      trend: estimatedRunwayData.percentageChange,
      direction: estimatedRunwayData.trend,
      subText: `from last month`,
      valueTone: estimatedRunwayData.valueTone,
    },
    {
      label: 'Burn Rate',
      value: previousMonthBurnData.burnRateInPercentage || '0%',
      trend: previousMonthBurnData.absoluteChangeInBurn,
      direction: previousMonthBurnData.trend,
      subText: `from last month`,
      valueTone: previousMonthBurnData.valueTone,
      tooltipText: <p className="text-xs">Monthly burn as % of starting cash (based on the last calendar month)</p>,
    },
    {
      label: 'Revenue',
      value: revenueData.formattedRevenue,
      trend: revenueData.percentageChange,
      direction: revenueData.trend,
      subText: `from last month`,
      valueTone: revenueData.valueTone,
    },
  ];

  return (
    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {metrics.map((metric) => (
        <MetricCard
          key={metric.label}
          label={metric.label}
          value={metric.value}
          trend={metric.trend}
          direction={metric.direction}
          subText={metric.subText}
          valueTone={metric.valueTone}
          tooltipText={metric.tooltipText}
        />
      ))}
    </div>
  );
};

export default CashFlowMetrics;
